/**
 * CR2 — GCU Intake (cr2a). Step 2 of the GCU flow: upload initial company
 * documents, then send them to AI analysis.
 *
 * Layout: GCU stepper on top, per-category upload tiles (2-col) on the left,
 * an intake summary + "Kirim ke Analisa AI" CTA on the right. All states
 * (loading / empty / error / in-flight) are handled; mutations refetch the GCU
 * roll-up so the stepper + counts stay truthful.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, ShieldCheck, AlertTriangle, Loader2, FolderOpen } from 'lucide-react';
import type { CompanyProfile, DocumentSummary, DocType } from '@klinik/shared';
import { AppShell } from '../components/AppShell';
import { GcuStepper } from '../components/GcuStepper';
import { UploadTile } from '../components/UploadTile';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Toast } from '../components/ui/Toast';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fadeOnly, fadeRise, staggerContainer } from '../lib/motion';
import { INTAKE_DOC_TYPES } from '../lib/docs';
import {
  useDocuments,
  useGcu,
  analyzeDocuments,
  openDocument,
  deleteDocument,
} from '../lib/useDocuments';
import { useToast } from '../lib/useToast';

export function IntakeUpload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;
  const companyId = user?.companyId ?? null;

  const { documents, loading, error, reload, setDocuments } = useDocuments(companyId);
  const { gcu, reload: reloadGcu } = useGcu(companyId);
  const { toast, show, dismiss } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [companyName, setCompanyName] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ company: CompanyProfile | null }>('/companies/me');
      if (res.ok && res.data.company) setCompanyName(res.data.company.name);
    })();
  }, []);

  // Group docs by type once per change for the tiles + summary.
  const byType = useMemo(() => {
    const map = new Map<DocType, DocumentSummary[]>();
    for (const d of documents) {
      const list = map.get(d.docType) ?? [];
      list.push(d);
      map.set(d.docType, list);
    }
    return map;
  }, [documents]);

  const uploadedCount = documents.filter((d) => d.status === 'uploaded').length;
  const totalDocs = documents.length;
  // Categories that have at least one uploaded doc (drives the "filled" count).
  const categoriesFilled = INTAKE_DOC_TYPES.filter((t) => (byType.get(t)?.length ?? 0) > 0).length;

  function onUploaded(doc: DocumentSummary) {
    // Optimistic insert; reconcile GCU counts in the background.
    setDocuments((prev) => [...prev, doc]);
    void reloadGcu();
    show('success', `${doc.filename} terunggah.`);
  }

  async function onOpen(doc: DocumentSummary) {
    if (!companyId) return;
    const res = await openDocument(companyId, doc.id);
    if (!res.ok) show('error', res.message);
  }

  async function onDelete(doc: DocumentSummary) {
    if (!companyId) return;
    const prev = documents;
    setDocuments((p) => p.filter((d) => d.id !== doc.id)); // optimistic
    const res = await deleteDocument(companyId, doc.id);
    if (res.ok) {
      void reloadGcu();
      show('success', `${doc.filename} dihapus.`);
    } else {
      setDocuments(prev); // rollback
      show('error', res.message);
    }
  }

  async function onAnalyze() {
    if (!companyId || analyzing) return;
    setAnalyzing(true);
    const res = await analyzeDocuments(companyId);
    setAnalyzing(false);
    if (res.ok) {
      await Promise.all([reload(), reloadGcu()]);
      show('success', `${res.enqueued} dokumen dikirim ke analisa AI.`);
      navigate('/documents');
    } else {
      show('error', res.message);
    }
  }

  return (
    <AppShell active="gcu">
      <Toast toast={toast} onDismiss={dismiss} />
      <motion.div initial="hidden" animate="show" variants={staggerContainer}>
        <motion.div variants={variants} className="mb-4">
          <GcuStepper gcu={gcu} />
        </motion.div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Upload tiles */}
          <motion.div variants={variants} className="lg:col-span-2">
            <Card>
              <div className="mb-1 font-display text-lg font-bold text-slate-900">
                Langkah 2 — Upload Data Awal Perusahaan
              </div>
              <p className="mb-4 text-sm text-slate-500">
                Dokumen ini diolah AI (dengan review tim Nine Co) untuk menyusun kuesioner diagnosa.
                Format PDF atau Excel, maksimal 25 MB per file.
              </p>

              {!companyId ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Halaman intake ini untuk akun Owner. Buka melalui dasbor klien.
                </div>
              ) : loading ? (
                <div className="h-48">
                  <Spinner label="Memuat dokumen…" />
                </div>
              ) : error ? (
                <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
                  <div className="flex-1">
                    <div className="font-semibold">Gagal memuat dokumen</div>
                    <div className="mt-0.5">{error}</div>
                  </div>
                  <Button variant="ghost" onClick={() => void reload()}>
                    Coba lagi
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {INTAKE_DOC_TYPES.map((docType) => (
                    <UploadTile
                      key={docType}
                      companyId={companyId!}
                      docType={docType}
                      docs={byType.get(docType) ?? []}
                      onUploaded={onUploaded}
                      onOpen={(d) => void onOpen(d)}
                      onDelete={(d) => void onDelete(d)}
                      disabled={analyzing}
                    />
                  ))}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Intake summary + analyze CTA */}
          <motion.div variants={variants}>
            <Card>
              <div className="mb-3 text-sm font-semibold text-slate-800">Ringkasan Intake</div>
              <SummaryRow label="Perusahaan" value={companyName ?? '—'} />
              <SummaryRow label="Kategori terisi" value={`${categoriesFilled} / ${INTAKE_DOC_TYPES.length}`} />
              <SummaryRow label="Dokumen" value={`${totalDocs} file`} />
              <SummaryRow label="Siap dianalisa" value={`${uploadedCount} file`} />
              <SummaryRow label="Privasi" value="Terenkripsi (R2)" last />

              <div className="mt-2 flex gap-2 rounded-xl bg-blue-50 p-3 text-xs text-blue-800">
                <ShieldCheck size={16} className="mt-0.5 shrink-0" aria-hidden />
                <span>
                  Dokumen sensitif (keuangan, akad) disimpan terenkripsi &amp; terisolasi per
                  perusahaan.
                </span>
              </div>

              <div className="mt-4">
                <Button
                  onClick={() => void onAnalyze()}
                  disabled={analyzing || uploadedCount === 0}
                  icon={
                    analyzing ? (
                      <Loader2 size={15} className="animate-spin" aria-hidden />
                    ) : (
                      <Sparkles size={15} aria-hidden />
                    )
                  }
                  className="w-full"
                >
                  {analyzing ? 'Mengirim…' : 'Kirim ke Analisa AI'}
                </Button>
                {uploadedCount === 0 && !loading && (
                  <p className="mt-2 text-center text-[11px] text-slate-400">
                    Unggah minimal satu dokumen untuk memulai analisa.
                  </p>
                )}
              </div>

              <button
                onClick={() => navigate('/documents')}
                className="mt-3 flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand"
              >
                <FolderOpen size={14} aria-hidden />
                Lihat seluruh dokumen
              </button>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </AppShell>
  );
}

function SummaryRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      className={`mb-3 flex items-center justify-between pb-2 ${
        last ? '' : 'border-b border-slate-100'
      }`}
    >
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm font-semibold text-slate-700">{value}</span>
    </div>
  );
}
